import {
  ForecastMonthlyOverviewResponse,
  OverviewValueType,
  TransactionOverviewType,
} from '@ydistri/api-sdk';
import { plural } from '@ydistri/generic-utils';

const getForecastRow = (
  data: ForecastMonthlyOverviewResponse,
  confidence: number,
  forecastTransactionType: TransactionOverviewType,
) =>
  data.overviews.find(
    o =>
      o.confidence === confidence &&
      o.overviewValueType === OverviewValueType.Quantity &&
      o.transactionOverviewType === forecastTransactionType,
  );

export const computeFinalMonthOverview = (
  monthlyForecast: ForecastMonthlyOverviewResponse[] | undefined,
  confidence: number,
  forecastTransactionType: TransactionOverviewType,
  supply: number,
): number => {
  if (!monthlyForecast) return 0;

  let lowerMonthValue = 0;
  let lowerMonthValueIndex = 0;
  let crossingMonthIndex: number | null = null;

  let i = 0;
  while (i < monthlyForecast.length) {
    const forecastRow = getForecastRow(monthlyForecast[i], confidence, forecastTransactionType);
    if (!forecastRow) {
      i++;
      continue;
    }

    const nextMonthValue = lowerMonthValue + forecastRow.value;
    if (nextMonthValue > supply) {
      crossingMonthIndex = i;
      break;
    }

    lowerMonthValue = nextMonthValue;
    lowerMonthValueIndex = i;
    i++;
  }

  if (crossingMonthIndex !== null) {
    const crossingMonth = monthlyForecast[crossingMonthIndex];
    const crossingForecast = getForecastRow(crossingMonth, confidence, forecastTransactionType);
    const rangeDiff = crossingForecast?.value ?? 0;
    const valueDiff = supply - lowerMonthValue;

    if (rangeDiff > 0 && valueDiff < rangeDiff) {
      return crossingMonthIndex + valueDiff / rangeDiff;
    }
  } else if (lowerMonthValueIndex === monthlyForecast.length - 1) {
    return 6;
  }

  return lowerMonthValueIndex + 1;
};

interface FormattedFinalMonths {
  full: string;
  compact: string;
}

export const formatFinalMonths = (finalMonthOverview: number): FormattedFinalMonths => {
  if (finalMonthOverview <= 0) {
    return {
      full: `0 ${plural(0, 'month')}`,
      compact: '0 m',
    };
  }

  const finalMonths =
    finalMonthOverview < 6
      ? finalMonthOverview.toFixed(Math.floor(finalMonthOverview) === finalMonthOverview ? 0 : 1)
      : '6+';

  const pluralCount = finalMonthOverview >= 6 ? 6 : finalMonthOverview;
  return {
    full: `${finalMonths} ${plural(pluralCount, 'month')}`,
    compact: `${finalMonths} m`,
  };
};
